/**
 * Settings › AI editing (client-MCP plan 2026-10-20 §10.1, Q26, §6.4, §13.2; contract
 * `../apiv2/dashboard_ai_edit.openapi.yaml` `/tour_operator/ai_edit/`): for every operator the viewer manages (their
 * own; on a franchise parent, the licensees assigned to them for `ai_edit`, plus the parent for parent-level staff)
 * the MCP auto-apply switch and the ops lock that keeps it off, how a change made over MCP is applied, and the
 * guest-data opt-in (`mcp_pii_tools`, backend P1b-06, ORCH-11) with where guest data goes once it is on.
 *
 * Behind the explicit `ai_edit.manage` (`hasExplicitPerm`: Full access does not imply it), held per operator: the
 * backend lists only the operators the viewer holds it on. Turning either switch on also needs a session that passed
 * two-step sign-in or an SSO one (403 `session_mfa_required`, review W3 M4); turning it off is open to any session.
 * The guest-data opt-in has no ops lock, and turning it on asks the admin to confirm that the company's agreement with
 * its AI provider covers guest data: the PUT carries that confirmation (`acknowledged_provider_agreement`), and the
 * backend refuses on without it (400 `provider_agreement_not_acknowledged`). Each PUT carries only the switch being
 * turned (and, turning guest data on, that confirmation). Every call keeps the session on a 403
 * (`aiChanges/api.js`); the backend records each change in the configuration history and the company's Activity
 * log (`mcp_auto_apply_changed`, `mcp_pii_opt_in_changed`). An older backend without `mcp_pii_tools` shows no
 * guest-data card.
 */
import {
    Alert,
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Paper,
    Stack,
    Switch,
    TextField,
    Typography,
} from '@mui/material';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { DataAPIContext } from '../../api';
import PageMeta from '../../components/PageMeta';
import * as api from '../../notifications/aiChanges/api';
import { BASE as AI_CHANGES_PATH } from '../../notifications/aiChanges/parts';
import { ContextStore } from '../../store/contextStore';
import { MCP_PERMISSIONS } from '../../users/agroups/mcpPermissions';

const MANAGE = MCP_PERMISSIONS['ai_edit.manage'];
const EXPORT = MCP_PERMISSIONS['analytics.mcp_export'];
const REASON_MAX = 500;
/** Turning a switch on (backend 403 `session_mfa_required`, review W3 M4); turning it off is open to any session. */
const MFA_NEEDED = 'Turning auto-apply on needs a session signed in with two-step sign-in (or single sign-on).';
const PII_MFA_NEEDED = 'Turning guest data over MCP on needs a session signed in with two-step sign-in (or single sign-on).';
const MFA_AGAIN = 'Sign out, sign in again with your second factor, then turn it on: nothing changed.';
/** Turning guest data on without the provider-agreement confirmation (backend 400, review batch 3 M6). */
const AGREEMENT_NEEDED = (name) => `Guest data over MCP stays off for ${name}: turning it on needs your confirmation that your `
    + 'company\'s agreement with its AI provider covers your guests\' data. Tick it in the dialog, then turn it on again.';

/** The two switches of a row: auto-apply (Q26) and the guest-data opt-in (P1b-06). */
const AUTO_APPLY = 'auto_apply';
const PII = 'pii';
/** The backend sends the opt-in since P1b-06; an older one does not, and the page leaves it out. */
const hasPiiOptIn = (row) => typeof row.mcp_pii_tools === 'boolean';
/** A franchise parent or one of its licensees: each decides on guest data for itself. */
const inFranchise = (row) => !!(row.tour_operator.is_franchise || row.tour_operator.parent_id);

const NoPermission = () => (
    <Alert severity="info" sx={{ mb: 2 }}>
        AI editing settings need “{MANAGE}”, granted explicitly in an access group. Full access does not include it: ask an
        administrator to grant it.
    </Alert>
);

/** How a change made over MCP is applied (§10.1): what the switch changes, and what stays whatever it is. */
const RoutingRules = () => (
    <Paper variant="outlined" sx={{ p: 2 }} data-testid="routing-rules">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>How a change made over MCP is applied</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2">On a sandbox, it applies at once.</Typography>
            <Typography component="li" variant="body2">
                On production, it waits in AI changes › Approvals until someone allowed to approve it applies it: someone
                holding the “approve pending changes” permission of every kind of record it changes, on a tag of each
                record (on all tags for a revert, and for records every tag shares).
            </Typography>
            <Typography component="li" variant="body2">
                It applies at once instead when auto-apply is on, or when its author may approve all of it themselves.
            </Typography>
            <Typography component="li" variant="body2">
                Both need an AI client connection that signed in with a second factor (MFA or single sign-on); otherwise
                the change waits, and its author can approve it on the AI changes page.
            </Typography>
            <Typography component="li" variant="body2">
                Whatever the route, every change is listed in AI changes and can be reverted there; new notification
                channels start disabled; warnings are recorded, and approvers get a daily digest of the auto-applied
                changes that carried warnings.
            </Typography>
        </Box>
    </Paper>
);

/** Guest data over MCP (§6.4, §13.2, Q8): what the opt-in lets through, to whom, and where it goes from there. */
const GuestDataRules = ({ franchise }) => (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid="guest-data-rules">
        <Typography variant="subtitle1" sx={{ mb: 1 }}>Guest data over MCP: where it goes</Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.75 } }}>
            <Typography component="li" variant="body2">
                Off by default. While it is off, an AI client connected over MCP sees guest contacts masked, and none of
                the bodies of the requests your booking system sends us or of the webhooks we send.
            </Typography>
            <Typography component="li" variant="body2">
                Turned on, a production connection that signed in with a second factor (MFA or single sign-on) sees them:
                guests&apos; e-mail addresses and phone numbers, and those request and webhook bodies. Any other connection
                still sees them masked.
            </Typography>
            <Typography component="li" variant="body2">
                What your AI client receives reaches the AI provider behind it, under your company&apos;s agreement with
                that provider. Turn it on only when that agreement covers your guests&apos; data.
            </Typography>
            <Typography component="li" variant="body2">
                {'Each person still needs the permissions of the tools that read it; exporting guest lists also needs '
                    + `“${EXPORT}”.`}
            </Typography>
            {franchise && (
                <Typography component="li" variant="body2">
                    Each company decides for itself: a licensee does not inherit its franchise parent&apos;s choice.
                </Typography>
            )}
            <Typography component="li" variant="body2">
                Every change is recorded in the settings history and your company&apos;s Activity log.
            </Typography>
        </Box>
    </Paper>
);

const WORDS = {
    [AUTO_APPLY]: { what: 'auto-apply', title: 'Auto-apply', mfa: MFA_NEEDED },
    [PII]: { what: 'guest data over MCP', title: 'Guest data over MCP', mfa: PII_MFA_NEEDED },
};

const errorText = (e, name, kind = AUTO_APPLY) => {
    switch (e.code) {
        case 'ai_edit_auto_apply_locked':
            return { severity: 'warning', text: `Curated Planet operations locked auto-apply off for ${name}: nothing changed.` };
        case 'ai_edit_manage_required':
            return { severity: 'error', text: `You may no longer manage AI editing settings (“${MANAGE}”): nothing changed.` };
        case 'provider_agreement_not_acknowledged':
            return { severity: 'warning', text: AGREEMENT_NEEDED(name) };
        case 'session_mfa_required':
            // Review W3 M4: on needs a session that passed two-step sign-in, or an SSO one; off is open to any session.
            return { severity: 'warning', text: `${WORDS[kind].mfa} ${MFA_AGAIN}` };
        default:
            if (e.status === 404) {
                return { severity: 'error', text: `You no longer manage the AI editing settings of ${name}: nothing changed.` };
            }
            return { severity: 'error', text: `Could not change ${WORDS[kind].what} for ${name}: ${e.message}` };
    }
};

const ReasonField = ({ value, onChange }) => (
    <TextField
        label="Reason (optional, kept in the settings history)"
        fullWidth
        multiline
        minRows={2}
        value={value}
        inputProps={{ maxLength: REASON_MAX }}
        onChange={(ev) => onChange(ev.target.value)}
    />
);

/** Turning guest data over MCP on or off; on asks the admin to confirm what it means for their guests' data. */
const PiiConfirmDialog = ({ row, onCancel, onConfirm }) => {
    const [reason, setReason] = React.useState('');
    const [acknowledged, setAcknowledged] = React.useState(false);
    const name = row.tour_operator.name;
    const on = !row.mcp_pii_tools;
    return (
        <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{on ? `Turn guest data over MCP on for ${name}?` : `Turn guest data over MCP off for ${name}?`}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: on ? 1 : 2 }}>
                    {on
                        ? `AI clients your team connects over MCP will see the guest contacts of ${name}, and the bodies of `
                        + 'the requests and webhooks that carry guest details, when they signed in with a second factor. '
                        + 'What they see reaches the AI provider behind each client.'
                        : `AI clients connected over MCP will see guest contacts masked again, and none of the request and `
                        + `webhook bodies of ${name}.`}
                </DialogContentText>
                {on && (
                    <>
                        <DialogContentText variant="body2" sx={{ mb: 1 }}>
                            Turning it on needs a session signed in with two-step sign-in (or single sign-on).
                        </DialogContentText>
                        <FormControlLabel
                            sx={{ mb: 2, alignItems: 'flex-start' }}
                            control={(
                                <Checkbox
                                    checked={acknowledged}
                                    onChange={(ev) => setAcknowledged(ev.target.checked)}
                                    sx={{ pt: 0.25 }}
                                />
                            )}
                            label="I confirm that our agreement with the AI provider we use covers our guests' data."
                        />
                    </>
                )}
                <ReasonField value={reason} onChange={setReason} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button
                    variant="contained"
                    color={on ? 'warning' : 'primary'}
                    disabled={on && !acknowledged}
                    onClick={() => onConfirm(reason, { acknowledged: on && acknowledged })}
                >
                    {on ? 'Turn on' : 'Turn off'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** Turn on / off, with an optional reason for the configuration history. */
const ConfirmDialog = ({ row, onCancel, onConfirm }) => {
    const [reason, setReason] = React.useState('');
    const name = row.tour_operator.name;
    const on = !row.mcp_auto_apply;
    return (
        <Dialog open onClose={onCancel} maxWidth="sm" fullWidth>
            <DialogTitle>{on ? 'Turn MCP auto-apply on?' : 'Turn MCP auto-apply off?'}</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: on ? 1 : 2 }}>
                    {on
                        ? `Changes an AI client makes over MCP on ${name} will apply at once, without anyone approving them, `
                        + 'for everyone allowed to propose them, when the client signed in with a second factor. Every '
                        + 'change stays listed in AI changes and can be reverted.'
                        : `Changes made over MCP on ${name} will wait in AI changes › Approvals until someone allowed to `
                        + 'approve them applies them, unless their author may approve all of them.'}
                </DialogContentText>
                {on && (
                    <DialogContentText variant="body2" sx={{ mb: 2 }}>
                        Turning it on needs a session signed in with two-step sign-in (or single sign-on).
                    </DialogContentText>
                )}
                <ReasonField value={reason} onChange={setReason} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel}>Cancel</Button>
                <Button variant="contained" color={on ? 'warning' : 'primary'} onClick={() => onConfirm(reason)}>
                    {on ? 'Turn on' : 'Turn off'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

/** One operator's switch (`DashboardAIEditSettings`). */
const AutoApplyCard = ({ row, busy, message, onToggle }) => {
    const name = row.tour_operator.name;
    // Off is always allowed; on only while not locked (`can_change`).
    const disabled = busy || (!row.mcp_auto_apply && !row.can_change);
    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`auto-apply-${row.tour_operator.id}`}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1">{name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        MCP auto-apply: changes made over MCP apply without waiting for an approver.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {busy && <CircularProgress size={20} />}
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={!!row.mcp_auto_apply}
                                disabled={disabled}
                                onChange={() => onToggle(row)}
                                inputProps={{ 'aria-label': `MCP auto-apply for ${name}` }}
                            />
                        )}
                        label={row.mcp_auto_apply ? 'On' : 'Off'}
                    />
                </Stack>
            </Stack>
            {row.mcp_auto_apply_locked && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                    {`Curated Planet operations locked auto-apply off for ${name}: it stays off until they remove the lock. `
                        + 'A change still applies at once when its author may approve all of it.'}
                </Alert>
            )}
            {row.mcp_enabled === false && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                    {`AI editing over MCP is not enabled for ${name} yet (Curated Planet turns it on): the switch takes effect once it is.`}
                </Alert>
            )}
            {message && <Alert severity={message.severity} sx={{ mt: 1.5 }}>{message.text}</Alert>}
        </Paper>
    );
};

/** One operator's guest-data opt-in (`mcp_pii_tools`): no ops lock; on needs a two-step sign-in. */
const PiiCard = ({ row, busy, message, onToggle }) => {
    const name = row.tour_operator.name;
    return (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }} data-testid={`pii-${row.tour_operator.id}`}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
                <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle1">{name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                        Guest data over MCP: AI clients connected with a second factor see guest contacts and the bodies of
                        requests and webhooks.
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
                    {busy && <CircularProgress size={20} />}
                    <FormControlLabel
                        control={(
                            <Switch
                                checked={!!row.mcp_pii_tools}
                                disabled={busy}
                                onChange={() => onToggle(row)}
                                inputProps={{ 'aria-label': `Guest data over MCP for ${name}` }}
                            />
                        )}
                        label={row.mcp_pii_tools ? 'On' : 'Off'}
                    />
                </Stack>
            </Stack>
            {message && <Alert severity={message.severity} sx={{ mt: 1.5 }}>{message.text}</Alert>}
        </Paper>
    );
};

const AutoApplySettings = () => {
    const dataAPI = React.useContext(DataAPIContext);
    const [state, setState] = React.useState({ loading: true, rows: [], error: null });
    const [reload, setReload] = React.useState(0);
    const [confirming, setConfirming] = React.useState(null);
    const [busyId, setBusyId] = React.useState(null);
    const [messages, setMessages] = React.useState({});

    React.useEffect(() => {
        let alive = true;
        api.getAIEditSettings(dataAPI)
            .then((data) => alive && setState({ loading: false, rows: data?.results || [], error: null }))
            .catch((error) => alive && setState({ loading: false, rows: [], error }));
        return () => {
            alive = false;
        };
    }, [dataAPI, reload]);

    // Per switch: `${kind}-${operator id}`.
    const say = (key, message) => setMessages((prev) => ({ ...prev, [key]: message }));

    const confirm = async (reason, { acknowledged = false } = {}) => {
        const { row, kind } = confirming;
        setConfirming(null);
        const id = row.tour_operator.id;
        const name = row.tour_operator.name;
        const key = `${kind}-${id}`;
        const field = kind === PII ? 'mcp_pii_tools' : 'mcp_auto_apply';
        const want = !row[field];
        setBusyId(key);
        say(key, null);
        try {
            const answer = kind === PII
                ? await api.setPiiTools(dataAPI, id, { piiTools: want, acknowledgedProviderAgreement: acknowledged, reason })
                : await api.setAutoApply(dataAPI, id, { autoApply: want, reason });
            setState((prev) => ({ ...prev, rows: prev.rows.map((r) => (r.tour_operator.id === id ? { ...r, ...answer } : r)) }));
            const now = answer[field] ? 'on' : 'off';
            const title = WORDS[kind].title;
            say(key, answer.changed === false
                ? { severity: 'info', text: `${title} already was ${now} for ${name}.` }
                : { severity: 'success', text: `${title} is ${now} for ${name}.` });
        } catch (e) {
            say(key, errorText(e, name, kind));
            if (e.code === 'ai_edit_auto_apply_locked') setReload((n) => n + 1);
        } finally {
            setBusyId(null);
        }
    };

    if (state.loading) return <CircularProgress size={24} sx={{ mb: 2 }} />;
    if (state.error) {
        return state.error.status === 403
            ? <NoPermission />
            : <Alert severity="error" sx={{ mb: 2 }}>{`Could not load the AI editing settings: ${state.error.message}`}</Alert>;
    }
    return (
        <Box>
            {state.rows.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    There is no company whose AI editing settings you manage here.
                </Typography>
            )}
            {state.rows.map((row) => {
                const id = row.tour_operator.id;
                return (
                    <React.Fragment key={id}>
                        <AutoApplyCard
                            row={row}
                            busy={busyId === `${AUTO_APPLY}-${id}`}
                            message={messages[`${AUTO_APPLY}-${id}`]}
                            onToggle={(r) => setConfirming({ row: r, kind: AUTO_APPLY })}
                        />
                        {hasPiiOptIn(row) && (
                            <PiiCard
                                row={row}
                                busy={busyId === `${PII}-${id}`}
                                message={messages[`${PII}-${id}`]}
                                onToggle={(r) => setConfirming({ row: r, kind: PII })}
                            />
                        )}
                    </React.Fragment>
                );
            })}
            {state.rows.some(hasPiiOptIn) && <GuestDataRules franchise={state.rows.some(inFranchise)} />}
            {confirming?.kind === AUTO_APPLY && (
                <ConfirmDialog row={confirming.row} onCancel={() => setConfirming(null)} onConfirm={confirm} />
            )}
            {confirming?.kind === PII && (
                <PiiConfirmDialog row={confirming.row} onCancel={() => setConfirming(null)} onConfirm={confirm} />
            )}
        </Box>
    );
};

const LIST_GRANTS = ['mcp_list', 'mcp_read', 'mcp_write', 'mcp_approve'];

const AIEditingSettingsPage = () => {
    const { hasExplicitPerm } = React.useContext(ContextStore) || {};
    const explicit = (scope, action) => typeof hasExplicitPerm === 'function' && hasExplicitPerm(scope, action);
    const seesChanges = LIST_GRANTS.some((action) => explicit('notification', action));
    return (
        <Box p={2} sx={{ maxWidth: 900 }}>
            <PageMeta title="AI editing" />
            <Typography variant="h6">AI editing</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                How the changes an AI client makes over MCP reach your account, and whether it may see guest data.
                {seesChanges && (
                    <>
                        {' '}Its changes are listed in <RouterLink to={AI_CHANGES_PATH}>AI changes</RouterLink>.
                    </>
                )}
            </Typography>
            {explicit('ai_edit', 'manage') ? <AutoApplySettings /> : <NoPermission />}
            <RoutingRules />
        </Box>
    );
};

export default AIEditingSettingsPage;
